<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Sparkles } from 'lucide-vue-next';
import { useUiStore } from '@/stores/uiStore';

const { t } = useI18n();
const ui = useUiStore();

// Hide while the sheet is open — the FAB sits in front of the overlay and
// would otherwise clip the slide-over visually.
const visible = computed(() => !ui.copilotOpen);

const isMac = typeof navigator !== 'undefined' && navigator.platform.toLowerCase().includes('mac');
const shortcut = isMac ? '⌘K' : 'Ctrl K';
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-apple"
    leave-active-class="transition-all duration-200 ease-apple"
    enter-from-class="opacity-0 translate-y-2 scale-95"
    leave-to-class="opacity-0 translate-y-2 scale-95"
  >
    <button
      v-if="visible"
      type="button"
      :aria-label="t('components.copilotFab.ariaLabel', { shortcut })"
      class="copilot-fab group fixed bottom-6 right-6 z-30 inline-flex items-center gap-2.5 h-12 pl-3 pr-4 rounded-full text-white font-medium text-[14px] shadow-pop transition-transform duration-200 ease-apple hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/40"
      @click="ui.openCopilot()"
    >
      <span class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
        <Sparkles class="h-4 w-4" />
      </span>
      <span class="hidden sm:inline">{{ t('components.copilotFab.label') }}</span>
      <kbd
        class="hidden md:inline-flex h-5 items-center rounded border border-white/30 bg-white/10 px-1.5 text-[10px] font-mono"
      >
        {{ shortcut }}
      </kbd>
    </button>
  </Transition>
</template>

<style scoped>
.copilot-fab {
  background: linear-gradient(135deg, #0a84ff 0%, #5e5ce6 50%, #af52de 100%);
}
.copilot-fab::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 9999px;
  padding: 1px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0));
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
</style>
